import { useState, useEffect, useRef } from 'react';
import { ref, onValue, set, remove, get } from 'firebase/database';
import { db } from '../firebase';

/**
 * イベントのstaff_membersを管理するフック
 * staff_members/{staffUid}: { joined_at, memo, invited_by }
 */
export const useStaffMembers = (eventId) => {
  const [staffMembers, setStaffMembers] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!eventId) {
      setStaffMembers({});
      setLoading(false);
      return;
    }

    const staffRef = ref(db, `events/${eventId}/staff_members`);
    const unsubscribe = onValue(staffRef, (snapshot) => {
      setStaffMembers(snapshot.val() || {});
      setLoading(false);
    });

    return () => unsubscribe();
  }, [eventId]);

  const addStaffMember = async (staffUid, { invitedBy }) => {
    const memberRef = ref(db, `events/${eventId}/staff_members/${staffUid}`);
    await set(memberRef, {
      joined_at: Date.now(),
      memo: '',
      invited_by: invitedBy,
    });
  };

  const updateMemo = async (staffUid, memo) => {
    const memoRef = ref(db, `events/${eventId}/staff_members/${staffUid}/memo`);
    await set(memoRef, memo);
  };

  const removeStaffMember = async (staffUid) => {
    const memberRef = ref(db, `events/${eventId}/staff_members/${staffUid}`);
    await remove(memberRef);

    // role が staff の場合のみ格下げ対象
    const userRoleSnap = await get(ref(db, `users/${staffUid}/role`));
    if (userRoleSnap.val() !== 'staff') return;

    // 他のイベントに所属していなければ guest に格下げ
    const allEventsSnap = await get(ref(db, 'events'));
    const allEvents = allEventsSnap.val() || {};
    const stillInAnyEvent = Object.entries(allEvents).some(
      ([eid, ev]) => eid !== eventId && ev?.staff_members?.[staffUid]
    );
    if (!stillInAnyEvent) {
      await set(ref(db, `users/${staffUid}/role`), 'guest');
    }
  };

  return { staffMembers, loading, addStaffMember, updateMemo, removeStaffMember };
};

/**
 * 複数イベントのスタッフを一括取得（スタッフ管理タブ用）
 * events: { [eventId]: eventData } の形式
 */
export const useAllStaffMembers = (events) => {
  const [staffByEvent, setStaffByEvent] = useState({});
  const [loading, setLoading] = useState(true);
  const dataRef = useRef({});

  useEffect(() => {
    const eventIds = Object.keys(events || {});
    if (eventIds.length === 0) {
      setStaffByEvent({});
      setLoading(false);
      return;
    }

    setLoading(true);
    dataRef.current = {};
    // Setで「初回ロード済み」を管理。全イベント分ロードされたら loading=false
    const initialLoaded = new Set();

    const unsubscribes = eventIds.map((eventId) => {
      const staffRef = ref(db, `events/${eventId}/staff_members`);
      return onValue(staffRef, (snapshot) => {
        dataRef.current = { ...dataRef.current, [eventId]: snapshot.val() || {} };
        initialLoaded.add(eventId);
        // 全イベント分揃ったら表示、以降は差分があるたびに即時反映
        if (initialLoaded.size === eventIds.length) {
          setStaffByEvent({ ...dataRef.current });
          setLoading(false);
        }
      });
    });

    return () => unsubscribes.forEach((u) => u());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(Object.keys(events || {}))]);

  return { staffByEvent, loading };
};
